/**
 * Master Taxonomy Registry — MeesterJay 4.1 MVP (Canonical)
 *
 * ============================================================
 * DEFINITIVE SOURCE OF TRUTH
 * ============================================================
 *
 * HIERARCHY: Module → Skill → SubSkill → FamilyId
 *
 * 8 Modules, ~38 Skills (module-scoped), 244 SubSkills (1:1 with Families), 16 Tiers
 *
 * MODULES (244 families total — MVP VERSION):
 *   GET  (52) - Getalbegrip
 *   MET  (39) - Metriek & Meetkunde
 *   TGL  (31) - Tijd & Geld
 *   SWW  (24) - Spelling Werkwoorden
 *   SPR  (20) - Spelling Regels
 *   TAA  (35) - Taalbegrip
 *   TOPO (21) - Topografie
 *   STU  (21) - Studievaardigheden
 *
 * MVP SCOPE: Auto-assessable families only (no open writing, no complex custom UI)
 *
 * SKILL HIERARCHY RULES:
 * - Module: 8 fixed modules (broad subject areas)
 * - Skill: ~5-10 per module, thematic groupings (e.g., "breuken", "klokkijken")
 *   **CRITICAL: Skills are MODULE-SCOPED** (e.g., SpellingWW::leenwoorden ≠ SpellingRegel::leenwoorden)
 * - SubSkill: Specific competency, 1:1 mapping with familyId
 * - FamilyId: Unique identifier (UPPERCASE-HYPHEN format)
 *
 * OVERLAP BEHAVIOR:
 * ✅ Skills CAN overlap WITHIN a module: Multiple families per skill (e.g., "breuken" has 13 families)
 * ❌ Skills are MODULE-SCOPED: Same skill name in different modules = different skills
 * ❌ SubSkills NEVER overlap: 1 subSkill = 1 familyId (strict 1:1)
 * ❌ FamilyIds NEVER overlap: Absolutely unique across all modules
 *
 * MODULE-SCOPED SKILLS:
 * Skills are namespaced by module to prevent collisions:
 * - "SpellingWW::leenwoorden" (3 families about verb conjugation)
 * - "SpellingRegel::leenwoorden" (5 families about loanword spelling)
 * These are DIFFERENT skills despite sharing the same name.
 *
 * Tier ↔ Niveau sync is enforced by tiers.ts.
 * Every familyId has exactly one module, skill, subSkill, tier, levelCode.
 *
 * SOURCE: TAXONOMY_SKILL_MAPPING.md v2.0 + FAMILY_ID_MASTER_PLAN.md + ChatGPT Validation
 * LAST UPDATED: 2026-02-19 - MVP version: Removed 8 families (TAA writing, STU complex, TOPO custom UI), added 1 (GET estimation) = 244 total
 * ============================================================
 */

import { ModuleId } from '../types'
import { tierToNiveauCode, isValidTier, isTierNiveauSynced } from './tiers'
import { debug, error as logError } from '../utils/logger'

// ============================================================
// Types
// ============================================================

export interface FamilyDefinition {
  familyId: string
  module: ModuleId
  skill: string
  subSkill: string
  tier: number
  levelCode: string
  description: string
  /** Direct prerequisite familyIds (Phase 1.5 — LDG foundation) */
  prerequisites?: string[]
}

// ============================================================
// MASTER TAXONOMY 4.1 — CANONICAL INDEX (244 FAMILIES)
// ============================================================
//
// ORGANIZED BY MODULE → SKILL HIERARCHY
// Each section documents the skill grouping and family count
//
// Skills are thematic groupings (e.g., "breuken", "klokkijken")
// SubSkills are specific competencies (1:1 with familyId)
// ============================================================

export const TAXONOMY: FamilyDefinition[] = [
  // ──────────────────────────────────────────────────────────
  // 1. MODULE: Getalbegrip (GET) — 52 families, 9 skills
  // ──────────────────────────────────────────────────────────
  // Skills: positiestelsel (6), breuken (13), kommagetallen (10),
  //         procenten (8), bewerkingen (3), verhoudingen (4),
  //         negatieve_getallen (3), machten_wortels (3), wetenschappelijk (2)

  // 1.1 Skill: positiestelsel (6 families)
  // Groot positiestelsel, lezen/schrijven, vergelijken, afronden, schatten
  {
    familyId: 'GET-GROOT-POS',
    module: 'Getalbegrip',
    skill: 'positiestelsel',
    subSkill: 'groot_positiestelsel',
    tier: 1,
    levelCode: 'B6',
    description: 'Positiestelsel: waarde van cijfers (M/D/H/T/E)',
  },
  {
    familyId: 'GET-GROOT-LEZEN',
    module: 'Getalbegrip',
    skill: 'positiestelsel',
    subSkill: 'groot_lezen_schrijven',
    tier: 1,
    levelCode: 'B6',
    description: 'Grote getallen lezen en schrijven',
  },
  {
    familyId: 'GET-GROOT-VERGELIJK',
    module: 'Getalbegrip',
    skill: 'positiestelsel',
    subSkill: 'groot_vergelijken',
    tier: 2,
    levelCode: 'B6+',
    description: 'Grote getallen vergelijken',
  },
  {
    familyId: 'GET-GROOT-AFRONDEN',
    module: 'Getalbegrip',
    skill: 'positiestelsel',
    subSkill: 'groot_afronden_basis',
    tier: 3,
    levelCode: 'M6',
    description: 'Afronden op tientallen/honderdtallen',
  },
  {
    familyId: 'GET-ROND-BASIS',
    module: 'Getalbegrip',
    skill: 'positiestelsel',
    subSkill: 'afronden_tientallen_honderdtallen',
    tier: 5,
    levelCode: 'E6',
    description: 'Afronden op tientallen en honderdtallen',
  },
  {
    familyId: 'GET-GROOT-SCHATTEN',
    module: 'Getalbegrip',
    skill: 'positiestelsel',
    subSkill: 'groot_schatten',
    tier: 7,
    levelCode: 'B7',
    description: 'Schatten en globaal rekenen',
  },
  // 1.2 Skill: breuken (13 families)
  // Basis visueel, lezen/schrijven, optellen/aftrekken (gelijknamig/ongelijknamig),
  // vergelijken, getallenlijn, vereenvoudigen, uitbreiden, vermenigvuldigen, delen, gemengd
  {
    familyId: 'GET-BREUK-BASIS',
    module: 'Getalbegrip',
    skill: 'breuken',
    subSkill: 'breuk_basis_visueel',
    tier: 1,
    levelCode: 'B6',
    description: 'Breuk als deel van geheel (visueel)',
  },
  {
    familyId: 'GET-BREUK-LEZEN',
    module: 'Getalbegrip',
    skill: 'breuken',
    subSkill: 'breuk_lezen_schrijven',
    tier: 1,
    levelCode: 'B6',
    description: 'Breuken lezen en schrijven',
  },
  {
    familyId: 'GET-BREUK-GEL-PLUS',
    module: 'Getalbegrip',
    skill: 'breuken',
    subSkill: 'breuk_optellen_gel',
    tier: 2,
    levelCode: 'B6+',
    description: 'Breuken optellen gelijknamig (teller+teller, noemer blijft)',
  },
  {
    familyId: 'GET-BREUK-GEL-MIN',
    module: 'Getalbegrip',
    skill: 'breuken',
    subSkill: 'breuk_aftrekken_gel',
    tier: 2,
    levelCode: 'B6+',
    description: 'Gelijknamige breuken aftrekken',
  },
  {
    familyId: 'GET-VERGELIJK',
    module: 'Getalbegrip',
    skill: 'breuken',
    subSkill: 'breuk_vergelijken',
    tier: 3,
    levelCode: 'M6',
    description: 'Breuken vergelijken',
  },
  {
    familyId: 'GET-BREUK-GETALLENLIJN',
    module: 'Getalbegrip',
    skill: 'breuken',
    subSkill: 'breuk_getallenlijn',
    tier: 5,
    levelCode: 'E6',
    description: 'Breuken op getallenlijn (CITO essentieel)',
  },
  {
    familyId: 'GET-BREUK-VEREENVOUDIG',
    module: 'Getalbegrip',
    skill: 'breuken',
    subSkill: 'breuk_vereenvoudigen',
    tier: 5,
    levelCode: 'E6',
    description: 'Breuken vereenvoudigen',
  },
  {
    familyId: 'GET-BREUK-UITBREIDEN',
    module: 'Getalbegrip',
    skill: 'breuken',
    subSkill: 'breuk_uitbreiden',
    tier: 5,
    levelCode: 'E6',
    description: 'Breuken uitbreiden',
  },
  {
    familyId: 'GET-BREUK-ONG-PLUS',
    module: 'Getalbegrip',
    skill: 'breuken',
    subSkill: 'breuk_optellen_ong',
    tier: 7,
    levelCode: 'B7',
    description:
      'Breuken optellen ongelijknamig (eerst gelijknamig maken via KGV)',
  },
  {
    familyId: 'GET-BREUK-MIN',
    module: 'Getalbegrip',
    skill: 'breuken',
    subSkill: 'breuk_aftrekken_ong',
    tier: 7,
    levelCode: 'B7',
    description: 'Ongelijknamige breuken aftrekken',
  },
  {
    familyId: 'GET-BREUK-KEER',
    module: 'Getalbegrip',
    skill: 'breuken',
    subSkill: 'breuk_vermenigvuldigen',
    tier: 9,
    levelCode: 'M7',
    description: 'Breuken vermenigvuldigen (teller × teller, noemer × noemer)',
  },
  {
    familyId: 'GET-BREUK-DEEL',
    module: 'Getalbegrip',
    skill: 'breuken',
    subSkill: 'breuk_delen',
    tier: 13,
    levelCode: 'B8',
    description: 'Breuken delen (keer het omgekeerde)',
  },
  {
    familyId: 'GET-BREUK-GEMENGD',
    module: 'Getalbegrip',
    skill: 'breuken',
    subSkill: 'breuk_gemengd',
    tier: 11,
    levelCode: 'E7',
    description: 'Gemengde getallen',
  },
  // 1.3 Skill: kommagetallen (10 families)
  // Decimale waarde, lezen/schrijven, vergelijken, getallenlijn,
  // optellen, aftrekken, vermenigvuldigen, delen, delen door komma, afronden
  {
    familyId: 'GET-KOMMA-WAARDE',
    module: 'Getalbegrip',
    skill: 'kommagetallen',
    subSkill: 'komma_decimale_waarde',
    tier: 5,
    levelCode: 'E6',
    description: 'Kommagetallen: begrip van tienden/honderdsten',
  },
  {
    familyId: 'GET-KOMMA-LEZEN',
    module: 'Getalbegrip',
    skill: 'kommagetallen',
    subSkill: 'komma_lezen_schrijven',
    tier: 5,
    levelCode: 'E6',
    description: 'Kommagetallen lezen en schrijven',
  },
  {
    familyId: 'GET-KOMMA-VERGELIJK',
    module: 'Getalbegrip',
    skill: 'kommagetallen',
    subSkill: 'komma_vergelijken',
    tier: 6,
    levelCode: 'E6+',
    description: 'Kommagetallen vergelijken',
  },
  {
    familyId: 'GET-KOMMA-GETALLENLIJN',
    module: 'Getalbegrip',
    skill: 'kommagetallen',
    subSkill: 'komma_getallenlijn',
    tier: 6,
    levelCode: 'E6+',
    description: 'Kommagetallen op getallenlijn (CITO essentieel)',
  },
  {
    familyId: 'GET-KOMMA-PLUS',
    module: 'Getalbegrip',
    skill: 'kommagetallen',
    subSkill: 'komma_optellen',
    tier: 7,
    levelCode: 'B7',
    description: 'Kommagetallen optellen',
  },
  {
    familyId: 'GET-KOMMA-MIN',
    module: 'Getalbegrip',
    skill: 'kommagetallen',
    subSkill: 'komma_aftrekken',
    tier: 7,
    levelCode: 'B7',
    description: 'Kommagetallen aftrekken',
  },
  {
    familyId: 'GET-KOMMA-KEER',
    module: 'Getalbegrip',
    skill: 'kommagetallen',
    subSkill: 'komma_vermenigvuldigen',
    tier: 9,
    levelCode: 'M7',
    description: 'Kommagetallen vermenigvuldigen',
  },
  {
    familyId: 'GET-KOMMA-DEEL',
    module: 'Getalbegrip',
    skill: 'kommagetallen',
    subSkill: 'komma_delen',
    tier: 11,
    levelCode: 'E7',
    description: 'Kommagetallen delen',
  },
  {
    familyId: 'GET-DEEL-KOMMA',
    module: 'Getalbegrip',
    skill: 'kommagetallen',
    subSkill: 'delen_door_kommagetal',
    tier: 11,
    levelCode: 'E7',
    description: 'Delen door kommagetal (CITO eindtoets)',
  },
  {
    familyId: 'GET-KOMMA-AFRONDEN',
    module: 'Getalbegrip',
    skill: 'kommagetallen',
    subSkill: 'komma_afronden',
    tier: 9,
    levelCode: 'M7',
    description: 'Kommagetallen afronden',
  },
  // 1.4 Skill: procenten (8 families)
  // Breuk/decimaal koppeling, percentage van bedrag, prijsverandering,
  // toename/afname, terugrekenen, BTW
  {
    familyId: 'GET-PROC-BASIS',
    module: 'Getalbegrip',
    skill: 'procenten',
    subSkill: 'proc_breuk_decimaal',
    tier: 6,
    levelCode: 'E6+',
    description:
      'Procenten basis: drieledige koppeling procent ↔ breuk ↔ decimaal',
  },
  {
    familyId: 'GET-PROC-VAN',
    module: 'Getalbegrip',
    skill: 'procenten',
    subSkill: 'proc_van_bedrag',
    tier: 9,
    levelCode: 'M7',
    description: 'Percentage van een bedrag berekenen (1%-regel)',
  },
  {
    familyId: 'GET-VERH-BREUK-PROC-KOPPEL',
    module: 'Getalbegrip',
    skill: 'procenten',
    subSkill: 'verh_breuk_proc_koppeling',
    tier: 9,
    levelCode: 'M7',
    description: 'Breuk ↔ procent ↔ decimaal toepassen (kerndoel)',
  },
  {
    familyId: 'GET-PROC-NIEUW',
    module: 'Getalbegrip',
    skill: 'procenten',
    subSkill: 'proc_prijsverandering',
    tier: 11,
    levelCode: 'E7',
    description: 'Korting of opslag verwerken (nieuwe prijs berekenen)',
  },
  {
    familyId: 'GET-PROC-TOENAME',
    module: 'Getalbegrip',
    skill: 'procenten',
    subSkill: 'proc_toename',
    tier: 13,
    levelCode: 'B8',
    description: 'Procentuele toename',
  },
  {
    familyId: 'GET-PROC-AFNAME',
    module: 'Getalbegrip',
    skill: 'procenten',
    subSkill: 'proc_afname',
    tier: 13,
    levelCode: 'B8',
    description: 'Procentuele afname',
  },
  {
    familyId: 'GET-PROC-TERUG',
    module: 'Getalbegrip',
    skill: 'procenten',
    subSkill: 'proc_terugrekenen',
    tier: 15,
    levelCode: 'E8',
    description: 'Terugrekenen: van deel naar geheel (100%)',
  },
  {
    familyId: 'GET-PROC-BTW',
    module: 'Getalbegrip',
    skill: 'procenten',
    subSkill: 'proc_btw_excl',
    tier: 16,
    levelCode: 'E8+',
    description: 'BTW exclusief berekenen: delen door 1.xx factor',
  },
  // Added Session 7 — compound percentage questions
  {
    familyId: 'GET-PROC-SAMENGESTELD',
    module: 'Getalbegrip',
    skill: 'procenten',
    subSkill: 'proc_samengesteld',
    tier: 9,
    levelCode: 'M7',
    description:
      'Samengestelde procentberekeningen: korting, stijging, dubbele korting, val-distractors',
  },
  // 1.5 Skill: bewerkingen (3 families)
  // Volgorde bewerkingen, hoofdrekenen strategieën, schatstrategie
  {
    familyId: 'GET-VOLGORDE-BEWERKING',
    module: 'Getalbegrip',
    skill: 'bewerkingen',
    subSkill: 'volgorde_bewerkingen',
    tier: 9,
    levelCode: 'M7',
    description: 'Haakjes en volgorde bewerkingen (CITO eindtoets)',
  },
  {
    familyId: 'GET-HOOFDREKENEN',
    module: 'Getalbegrip',
    skill: 'bewerkingen',
    subSkill: 'hoofdrekenen_strategieen',
    tier: 5,
    levelCode: 'E6',
    description: 'Hoofdrekenen strategieën',
  },
  {
    familyId: 'GET-SCHATSTRATEGIE',
    module: 'Getalbegrip',
    skill: 'bewerkingen',
    subSkill: 'schatstrategie',
    tier: 6,
    levelCode: 'E6+',
    description:
      'Rekenuitkomst schatten vóór exact uitrekenen (afronden, referentiegetallen)',
  },
  // 1.6 Skill: verhoudingen (4 families)
  // Verhoudingstabel, schaal op kaart, recepten, verdelen in verhouding
  {
    familyId: 'GET-VERH-SCHAAL',
    module: 'Getalbegrip',
    skill: 'verhoudingen',
    subSkill: 'verhoudingstabel',
    tier: 10,
    levelCode: 'M7+',
    description: 'Verhoudingstabel: kruislings vermenigvuldigen',
  },
  {
    familyId: 'GET-VERH-SCHAAL-KAART',
    module: 'Getalbegrip',
    skill: 'verhoudingen',
    subSkill: 'verh_schaal_kaart',
    tier: 11,
    levelCode: 'E7',
    description: 'Schaal op kaarten',
  },
  {
    familyId: 'GET-VERH-RECEPT',
    module: 'Getalbegrip',
    skill: 'verhoudingen',
    subSkill: 'verh_recept',
    tier: 9,
    levelCode: 'M7',
    description: 'Verhoudingen in recepten',
  },
  {
    familyId: 'GET-VERH-VERDELEN',
    module: 'Getalbegrip',
    skill: 'verhoudingen',
    subSkill: 'verh_verdelen',
    tier: 11,
    levelCode: 'E7',
    description: 'Verdelen in verhouding',
  },
  // 1.7 Skill: negatieve_getallen (3 families)
  // Getallenlijn, optellen, aftrekken met negatieve getallen
  {
    familyId: 'GET-NEGATIEF',
    module: 'Getalbegrip',
    skill: 'negatieve_getallen',
    subSkill: 'negatief_getallenlijn',
    tier: 11,
    levelCode: 'E7',
    description: 'Negatieve getallen op getallenlijn',
  },
  {
    familyId: 'GET-NEGATIEF-PLUS',
    module: 'Getalbegrip',
    skill: 'negatieve_getallen',
    subSkill: 'negatief_optellen',
    tier: 13,
    levelCode: 'B8',
    description: 'Optellen met negatieve getallen',
  },
  {
    familyId: 'GET-NEGATIEF-MIN',
    module: 'Getalbegrip',
    skill: 'negatieve_getallen',
    subSkill: 'negatief_aftrekken',
    tier: 13,
    levelCode: 'B8',
    description: 'Aftrekken met negatieve getallen',
  },
  // 1.8 Skill: machten_wortels (3 families)
  // Machtsverheffen basis, grondtal, worteltrekken
  {
    familyId: 'GET-MACHT',
    module: 'Getalbegrip',
    skill: 'machten_wortels',
    subSkill: 'macht_basis',
    tier: 13,
    levelCode: 'B8',
    description: 'Machtsverheffen basis',
  },
  {
    familyId: 'GET-MACHT-GRONDTAL',
    module: 'Getalbegrip',
    skill: 'machten_wortels',
    subSkill: 'macht_grondtal',
    tier: 14,
    levelCode: 'M8',
    description: 'Machten met grondtal',
  },
  {
    familyId: 'GET-WORTEL',
    module: 'Getalbegrip',
    skill: 'machten_wortels',
    subSkill: 'wortel_basis',
    tier: 15,
    levelCode: 'E8',
    description: 'Worteltrekken basis',
  },
  // 1.9 Skill: wetenschappelijk (2 families)
  // Wetenschappelijke notatie, rekenen met wetenschappelijke notatie
  {
    familyId: 'GET-WETENSCHAPPELIJK',
    module: 'Getalbegrip',
    skill: 'wetenschappelijk',
    subSkill: 'wetenschap_notatie',
    tier: 16,
    levelCode: 'E8+',
    description: 'Wetenschappelijke notatie',
  },
  {
    familyId: 'GET-WETENSCHAP-REKEN',
    module: 'Getalbegrip',
    skill: 'wetenschappelijk',
    subSkill: 'wetenschap_rekenen',
    tier: 16,
    levelCode: 'E8+',
    description: 'Rekenen met wetenschappelijke notatie',
  },
  // ──────────────────────────────────────────────────────────
  // 2. MODULE: Metriek & Meetkunde (MET) — 39 families, 9 skills
  // ──────────────────────────────────────────────────────────
  // Skills: lengte (5), gewicht (4), inhoud (6), oppervlakte (8),
  //         cirkels (4), hoeken (4), figuren (3), schaal (3), tijd_meten (2)

  // 2.1 Skill: lengte (5 families)
  // Omrekenen (mm↔cm↔dm↔m↔km), schatten, meten, nauwkeurig meten, context
  {
    familyId: 'MET-LEN-OMREKEN',
    module: 'Metriek',
    skill: 'lengte',
    subSkill: 'lengte_omrekenen',
    tier: 1,
    levelCode: 'B6',
    description: 'Het metrieke stelsel (trapje): mm ↔ cm ↔ dm ↔ m ↔ km',
  },
  {
    familyId: 'MET-LEN-SCHATTEN',
    module: 'Metriek',
    skill: 'lengte',
    subSkill: 'lengte_schatten',
    tier: 2,
    levelCode: 'B6+',
    description: 'Lengtes schatten',
  },
  {
    familyId: 'MET-LEN-METEN',
    module: 'Metriek',
    skill: 'lengte',
    subSkill: 'lengte_meten',
    tier: 1,
    levelCode: 'B6',
    description: 'Lengtes meten met liniaal',
  },
  {
    familyId: 'MET-LEN-NAUWKEURIG',
    module: 'Metriek',
    skill: 'lengte',
    subSkill: 'lengte_nauwkeurig',
    tier: 5,
    levelCode: 'E6',
    description: 'Nauwkeurig meten (mm precisie)',
  },
  {
    familyId: 'MET-LEN-CONTEXT',
    module: 'Metriek',
    skill: 'lengte',
    subSkill: 'lengte_context',
    tier: 7,
    levelCode: 'B7',
    description: 'Lengtes in context (afstand, hoogte)',
  },
  // 2.2 Skill: gewicht (4 families)
  // Omrekenen (g/kg/ton), schatten, wegen, context
  {
    familyId: 'MET-GEW-OMREKEN',
    module: 'Metriek',
    skill: 'gewicht',
    subSkill: 'gewicht_omrekenen',
    tier: 3,
    levelCode: 'M6',
    description: 'Gewicht omrekenen (g/kg/ton)',
  },
  {
    familyId: 'MET-GEW-SCHATTEN',
    module: 'Metriek',
    skill: 'gewicht',
    subSkill: 'gewicht_schatten',
    tier: 3,
    levelCode: 'M6',
    description: 'Gewicht schatten',
  },
  {
    familyId: 'MET-GEW-WEGEN',
    module: 'Metriek',
    skill: 'gewicht',
    subSkill: 'gewicht_wegen',
    tier: 2,
    levelCode: 'B6+',
    description: 'Wegen met weegschaal',
  },
  {
    familyId: 'MET-GEW-CONTEXT',
    module: 'Metriek',
    skill: 'gewicht',
    subSkill: 'gewicht_context',
    tier: 7,
    levelCode: 'B7',
    description: 'Gewicht in context',
  },
  // 2.3 Skill: inhoud (6 families)
  // Omrekenen (ml/cl/dl/l), liter-brug (dm³=l), kubiek omrekenen,
  // kleine maten, inhoud balk, schatten
  {
    familyId: 'MET-INH-OMREKEN',
    module: 'Metriek',
    skill: 'inhoud',
    subSkill: 'inhoud_omrekenen',
    tier: 5,
    levelCode: 'E6',
    description: 'Inhoud omrekenen (ml/cl/dl/l)',
  },
  {
    familyId: 'MET-INH-LITER',
    module: 'Metriek',
    skill: 'inhoud',
    subSkill: 'inh_liter_brug',
    tier: 9,
    levelCode: 'M7',
    description: 'De koppeling dm³ = liter',
  },
  {
    familyId: 'MET-INH-OMREKEN-KUBIEK',
    module: 'Metriek',
    skill: 'inhoud',
    subSkill: 'inhoud_kubiek_omreken',
    tier: 11,
    levelCode: 'E7',
    description: 'm³ ↔ dm³ ↔ liter verdieping (kerndoel)',
  },
  {
    familyId: 'MET-INH-CITER',
    module: 'Metriek',
    skill: 'inhoud',
    subSkill: 'inh_kleine_maten',
    tier: 12,
    levelCode: 'E7+',
    description: 'Kleine inhoudsmaten: cc/cl/dl (fijnmazig omrekenen)',
  },
  {
    familyId: 'MET-INH-BLOK',
    module: 'Metriek',
    skill: 'inhoud',
    subSkill: 'inh_blok',
    tier: 6,
    levelCode: 'E6+',
    description: 'Inhoud balk: L × B × H',
  },
  {
    familyId: 'MET-INH-SCHATTEN',
    module: 'Metriek',
    skill: 'inhoud',
    subSkill: 'inhoud_schatten',
    tier: 7,
    levelCode: 'B7',
    description: 'Inhoud schatten',
  },
  // 2.4 Skill: oppervlakte (8 families)
  // Rechthoek (opp/omtrek), driehoek, samengesteld, landmaten,
  // vierkant, parallellogram, omrekenen
  {
    familyId: 'MET-OPP-RECHT',
    module: 'Metriek',
    skill: 'oppervlakte',
    subSkill: 'opp_rechthoek',
    tier: 5,
    levelCode: 'E6',
    description: 'Oppervlakte rechthoek: L × B formule',
  },
  {
    familyId: 'MET-OMTREK-RECHT',
    module: 'Metriek',
    skill: 'oppervlakte',
    subSkill: 'omtrek_rechthoek',
    tier: 5,
    levelCode: 'E6',
    description: 'Omtrek rechthoek: 2L + 2B formule',
  },
  {
    familyId: 'MET-OPP-DRIEHOEK',
    module: 'Metriek',
    skill: 'oppervlakte',
    subSkill: 'opp_driehoek',
    tier: 6,
    levelCode: 'E6+',
    description: 'Oppervlakte driehoek: basis × hoogte ÷ 2',
  },
  {
    familyId: 'MET-OPP-SAMENGESTELD',
    module: 'Metriek',
    skill: 'oppervlakte',
    subSkill: 'opp_samengesteld',
    tier: 7,
    levelCode: 'B7',
    description: 'Samengestelde figuren: delen en optellen',
  },
  {
    familyId: 'MET-OPP-LAND',
    module: 'Metriek',
    skill: 'oppervlakte',
    subSkill: 'opp_landmaten',
    tier: 10,
    levelCode: 'M7+',
    description: 'Landmaten: are/hectare conversie',
  },
  {
    familyId: 'MET-OPP-VIERKANT',
    module: 'Metriek',
    skill: 'oppervlakte',
    subSkill: 'opp_vierkant',
    tier: 5,
    levelCode: 'E6',
    description: 'Oppervlakte vierkant',
  },
  {
    familyId: 'MET-OPP-PARALLELLOGRAM',
    module: 'Metriek',
    skill: 'oppervlakte',
    subSkill: 'opp_parallellogram',
    tier: 9,
    levelCode: 'M7',
    description: 'Oppervlakte parallellogram',
  },
  {
    familyId: 'MET-OPP-OMREKENEN',
    module: 'Metriek',
    skill: 'oppervlakte',
    subSkill: 'opp_omrekenen',
    tier: 11,
    levelCode: 'E7',
    description: 'Oppervlakte-eenheden omrekenen',
  },
  // 2.5 Skill: cirkels (4 families)
  // Omtrek cirkel (π), oppervlakte cirkel, diameter/straal, cirkelsector
  {
    familyId: 'MET-MEETK-OMTREK',
    module: 'Metriek',
    skill: 'cirkels',
    subSkill: 'meetk_omtrek_cirkel',
    tier: 14,
    levelCode: 'M8',
    description: 'Omtrek cirkel: introductie van π',
  },
  {
    familyId: 'MET-MEETK-OPP',
    module: 'Metriek',
    skill: 'cirkels',
    subSkill: 'meetk_opp_cirkel',
    tier: 15,
    levelCode: 'E8',
    description: 'Oppervlakte cirkel: kwadratisch rekenen met π',
  },
  {
    familyId: 'MET-MEETK-DIAMETER',
    module: 'Metriek',
    skill: 'cirkels',
    subSkill: 'meetk_diameter_straal',
    tier: 13,
    levelCode: 'B8',
    description: 'Diameter en straal',
  },
  {
    familyId: 'MET-MEETK-CIRKEL-DEEL',
    module: 'Metriek',
    skill: 'cirkels',
    subSkill: 'meetk_cirkelsector',
    tier: 16,
    levelCode: 'E8+',
    description: 'Cirkelsector en -segment',
  },
  // 2.6 Skill: hoeken (4 families)
  // Meten, tekenen, soorten (recht/stomp/scherp), berekenen (som 180°)
  {
    familyId: 'MET-HOEK-METEN',
    module: 'Metriek',
    skill: 'hoeken',
    subSkill: 'hoek_meten',
    tier: 7,
    levelCode: 'B7',
    description: 'Hoeken meten met geodriehoek',
  },
  {
    familyId: 'MET-HOEK-TEKENEN',
    module: 'Metriek',
    skill: 'hoeken',
    subSkill: 'hoek_tekenen',
    tier: 7,
    levelCode: 'B7',
    description: 'Hoeken tekenen',
  },
  {
    familyId: 'MET-HOEK-SOORTEN',
    module: 'Metriek',
    skill: 'hoeken',
    subSkill: 'hoek_soorten',
    tier: 9,
    levelCode: 'M7',
    description: 'Hoeksoorten (recht/stomp/scherp)',
  },
  {
    familyId: 'MET-HOEK-BEREKENEN',
    module: 'Metriek',
    skill: 'hoeken',
    subSkill: 'hoek_berekenen',
    tier: 13,
    levelCode: 'B8',
    description: 'Hoeken berekenen (som 180°)',
  },
  // 2.7 Skill: figuren (3 families)
  // Driehoeksoorten, symmetrie/spiegeling, coördinaten/assenstelsel
  {
    familyId: 'MET-DRIEHOEK-SOORTEN',
    module: 'Metriek',
    skill: 'figuren',
    subSkill: 'driehoek_soorten',
    tier: 9,
    levelCode: 'M7',
    description: 'Gelijkzijdig/gelijkbenig/rechthoekig (kerndoel)',
  },
  {
    familyId: 'MET-SYMMETRIE',
    module: 'Metriek',
    skill: 'figuren',
    subSkill: 'symmetrie_spiegeling',
    tier: 7,
    levelCode: 'B7',
    description: 'Spiegeling en symmetrie (kerndoel)',
  },
  {
    familyId: 'MET-COORDINATEN',
    module: 'Metriek',
    skill: 'figuren',
    subSkill: 'coordinaten_assenstelsel',
    tier: 9,
    levelCode: 'M7',
    description: 'Assenstelsel basis (CITO)',
  },
  // 2.8 Skill: schaal (3 families)
  // Schaal basis (1:100), schaal bij lengte, schaal bij oppervlakte
  {
    familyId: 'MET-SCHAAL-BASIS',
    module: 'Metriek',
    skill: 'schaal',
    subSkill: 'schaal_basis',
    tier: 9,
    levelCode: 'M7',
    description: 'Schaal basis (1:100)',
  },
  {
    familyId: 'MET-SCHAAL-LENGTE',
    module: 'Metriek',
    skill: 'schaal',
    subSkill: 'schaal_lengte',
    tier: 11,
    levelCode: 'E7',
    description: 'Schaal bij lengte',
  },
  {
    familyId: 'MET-SCHAAL-OPP',
    module: 'Metriek',
    skill: 'schaal',
    subSkill: 'schaal_oppervlakte',
    tier: 16,
    levelCode: 'E8+',
    description: 'Schaal bij oppervlakte: schaal in het kwadraat',
  },
  // 2.9 Skill: tijd_meten (2 families)
  // Tijd meten met stopwatch, nauwkeurig tijdmeten
  {
    familyId: 'MET-TIJD-METEN',
    module: 'Metriek',
    skill: 'tijd_meten',
    subSkill: 'tijd_meten_stopwatch',
    tier: 3,
    levelCode: 'M6',
    description: 'Tijd meten met stopwatch',
  },
  {
    familyId: 'MET-TIJD-NAUWKEURIG',
    module: 'Metriek',
    skill: 'tijd_meten',
    subSkill: 'tijd_nauwkeurig_meten',
    tier: 7,
    levelCode: 'B7',
    description: 'Nauwkeurig tijdmeten (seconden/tienden)',
  },
  // ──────────────────────────────────────────────────────────
  // 3. MODULE: Tijd & Geld (TGL) — 31 families, 5 skills
  // ──────────────────────────────────────────────────────────
  // Skills: klokkijken (6), tijdrekenen (6), snelheid (3), geld_basis (6), geld_toegepast (9)
  // NOTE: "snelheid" is a separate skill for AST (Afstand-Snelheid-Tijd) formula families

  // 3.1 Skill: klokkijken (6 families)
  // Analoog (kwartieren/gedetailleerd), digitaal, conversie, 12/24-uurs, tijdzones
  {
    familyId: 'TGL-TIJD-ANALOOG',
    module: 'TijdGeld',
    skill: 'klokkijken',
    subSkill: 'klok_analoog',
    tier: 1,
    levelCode: 'B6',
    description: 'Analoge klok (kwartieren)',
  },
  {
    familyId: 'TGL-TIJD-DIGITAAL',
    module: 'TijdGeld',
    skill: 'klokkijken',
    subSkill: 'klok_digitaal',
    tier: 3,
    levelCode: 'M6',
    description: 'Digitale klok (24-uurs)',
  },
  {
    familyId: 'TGL-TIJD-ANALOOG-GEDETAILLEERD',
    module: 'TijdGeld',
    skill: 'klokkijken',
    subSkill: 'klok_analoog_gedetailleerd',
    tier: 3,
    levelCode: 'M6',
    description: 'Analoog 5-min precisie',
  },
  {
    familyId: 'TGL-TIJD-CONVERSIE',
    module: 'TijdGeld',
    skill: 'klokkijken',
    subSkill: 'klok_conversie',
    tier: 5,
    levelCode: 'E6',
    description: 'Analoog ↔ Digitaal',
  },
  {
    familyId: 'TGL-TIJD-12-24',
    module: 'TijdGeld',
    skill: 'klokkijken',
    subSkill: 'klok_12_24_notatie',
    tier: 7,
    levelCode: 'B7',
    description: '12-uurs ↔ 24-uurs notatie',
  },
  {
    familyId: 'TGL-TIJD-TIJDZONES',
    module: 'TijdGeld',
    skill: 'klokkijken',
    subSkill: 'klok_tijdzones',
    tier: 13,
    levelCode: 'B8',
    description: 'Tijdzones en wereldtijd',
  },
  // 3.2 Skill: tijdrekenen (6 families)
  // Duur berekenen, planning, schema's, diagrammen, kalender,
  // omrekenen, snelheid/afstand/gemiddelde (AST-formule)
  {
    familyId: 'TGL-TIJD-DUUR',
    module: 'TijdGeld',
    skill: 'tijdrekenen',
    subSkill: 'tijd_duur_berekenen',
    tier: 7,
    levelCode: 'B7',
    description: 'Tijdsduur berekenen',
  },
  {
    familyId: 'TGL-TIJD-PLANNING',
    module: 'TijdGeld',
    skill: 'tijdrekenen',
    subSkill: 'tijd_planning',
    tier: 9,
    levelCode: 'M7',
    description: 'Tijd plannen met context',
  },
  {
    familyId: 'TGL-TIJD-SCHEMA',
    module: 'TijdGeld',
    skill: 'tijdrekenen',
    subSkill: 'tijd_schema_lezen',
    tier: 11,
    levelCode: 'E7',
    description: "Schema's en roosters lezen",
  },
  {
    familyId: 'TGL-TIJD-DIAGRAM',
    module: 'TijdGeld',
    skill: 'tijdrekenen',
    subSkill: 'tijd_diagram',
    tier: 11,
    levelCode: 'E7',
    description: 'Tijddiagrammen interpreteren (CITO)',
  },
  {
    familyId: 'TGL-TIJD-KALENDER',
    module: 'TijdGeld',
    skill: 'tijdrekenen',
    subSkill: 'tijd_kalender',
    tier: 11,
    levelCode: 'E7',
    description: 'Kalenderberekeningen',
  },
  {
    familyId: 'TGL-TIJD-OMREKENEN',
    module: 'TijdGeld',
    skill: 'tijdrekenen',
    subSkill: 'tijd_omrekenen',
    tier: 14,
    levelCode: 'M8',
    description: 'Tijdseenheden omrekenen',
  },
  // Added Session 7 — compound time calculation questions
  {
    familyId: 'TGL-TIJD-SAMENGESTELD',
    module: 'TijdGeld',
    skill: 'tijdrekenen',
    subSkill: 'tijd_samengesteld',
    tier: 7,
    levelCode: 'B7',
    description:
      'Samengestelde tijdberekeningen: eindtijd, duur, totale werktijd',
  },
  // 3.3 Skill: snelheid (3 families)
  // Snelheid berekenen (AST), afstand berekenen, gemiddelde snelheid
  {
    familyId: 'TGL-TIJD-SNELHEID',
    module: 'TijdGeld',
    skill: 'snelheid',
    subSkill: 'snelheid_berekenen',
    tier: 13,
    levelCode: 'B8',
    description: 'Snelheid berekenen (AST)',
  },
  {
    familyId: 'TGL-TIJD-AFSTAND',
    module: 'TijdGeld',
    skill: 'snelheid',
    subSkill: 'afstand_berekenen',
    tier: 14,
    levelCode: 'M8',
    description: 'Afstand berekenen uit snelheid',
  },
  {
    familyId: 'TGL-TIJD-GEMIDDELDE',
    module: 'TijdGeld',
    skill: 'snelheid',
    subSkill: 'snelheid_gemiddelde',
    tier: 15,
    levelCode: 'E8',
    description: 'Gemiddelde snelheid',
  },
  // 3.4 Skill: geld_basis (6 families)
  // Bedrag maken, teruggeven, wisselgeld, kommagetallen, tellen, valuta
  {
    familyId: 'TGL-GELD-MAAK',
    module: 'TijdGeld',
    skill: 'geld_basis',
    subSkill: 'geld_bedrag_maken',
    tier: 2,
    levelCode: 'B6+',
    description: 'Bedrag samenstellen',
  },
  {
    familyId: 'TGL-GELD-TERUG',
    module: 'TijdGeld',
    skill: 'geld_basis',
    subSkill: 'geld_teruggeven',
    tier: 2,
    levelCode: 'B6+',
    description: 'Wisselgeld teruggeven',
  },
  {
    familyId: 'TGL-GELD-WISSEL',
    module: 'TijdGeld',
    skill: 'geld_basis',
    subSkill: 'geld_wisselgeld',
    tier: 4,
    levelCode: 'M6+',
    description: 'Wisselgeld berekenen',
  },
  {
    familyId: 'TGL-GELD-KOMMA',
    module: 'TijdGeld',
    skill: 'geld_basis',
    subSkill: 'geld_kommagetallen',
    tier: 7,
    levelCode: 'B7',
    description: 'Euro/cent notatie rekenen',
  },
  {
    familyId: 'TGL-GELD-TELLEN',
    module: 'TijdGeld',
    skill: 'geld_basis',
    subSkill: 'geld_tellen',
    tier: 1,
    levelCode: 'B6',
    description: 'Geld tellen en herkennen',
  },
  {
    familyId: 'TGL-GELD-WISSELEN',
    module: 'TijdGeld',
    skill: 'geld_basis',
    subSkill: 'geld_valuta',
    tier: 9,
    levelCode: 'M7',
    description: 'Valuta wisselen (basis)',
  },
  // 3.5 Skill: geld_toegepast (9 families)
  // Vergelijken, budget, korting, BTW, terugrekenen, sparen,
  // rente/procent, boodschappen, prijs per eenheid
  {
    familyId: 'TGL-GELD-KOOPJES',
    module: 'TijdGeld',
    skill: 'geld_toegepast',
    subSkill: 'geld_vergelijken',
    tier: 8,
    levelCode: 'B7+',
    description: 'Prijzen vergelijken',
  },
  {
    familyId: 'TGL-GELD-BUDGET',
    module: 'TijdGeld',
    skill: 'geld_toegepast',
    subSkill: 'geld_budget',
    tier: 10,
    levelCode: 'M7+',
    description: 'Budget beheren',
  },
  {
    familyId: 'TGL-GELD-KORTING',
    module: 'TijdGeld',
    skill: 'geld_toegepast',
    subSkill: 'geld_korting',
    tier: 10,
    levelCode: 'M7+',
    description: 'Korting berekenen',
  },
  {
    familyId: 'TGL-GELD-BTW',
    module: 'TijdGeld',
    skill: 'geld_toegepast',
    subSkill: 'geld_btw',
    tier: 15,
    levelCode: 'E8',
    description: 'BTW berekenen',
  },
  {
    familyId: 'TGL-GELD-TERUGREKENEN',
    module: 'TijdGeld',
    skill: 'geld_toegepast',
    subSkill: 'geld_terugrekenen',
    tier: 16,
    levelCode: 'E8+',
    description: 'Originele prijs berekenen',
  },
  {
    familyId: 'TGL-GELD-SPAREN',
    module: 'TijdGeld',
    skill: 'geld_toegepast',
    subSkill: 'geld_sparen',
    tier: 9,
    levelCode: 'M7',
    description: 'Sparen en rente (basis)',
  },
  {
    familyId: 'TGL-GELD-RENTE-PROC',
    module: 'TijdGeld',
    skill: 'geld_toegepast',
    subSkill: 'geld_rente_procent',
    tier: 13,
    levelCode: 'B8',
    description: 'Rente en procenten (toegepast)',
  },
  {
    familyId: 'TGL-GELD-BOODSCHAPPEN',
    module: 'TijdGeld',
    skill: 'geld_toegepast',
    subSkill: 'geld_boodschappen',
    tier: 7,
    levelCode: 'B7',
    description: 'Boodschappen doen (totaal)',
  },
  {
    familyId: 'TGL-GELD-PRIJS-PER',
    module: 'TijdGeld',
    skill: 'geld_toegepast',
    subSkill: 'geld_prijs_per_eenheid',
    tier: 11,
    levelCode: 'E7',
    description: 'Prijs per eenheid (kg/l/stuk)',
  },
  // ──────────────────────────────────────────────────────────
  // 4. MODULE: Spelling Werkwoorden (SWW) — 24 families, 5 skills
  // ──────────────────────────────────────────────────────────
  // Skills: tegenwoordige_tijd (7), verleden_tijd (7), voltooid_deelwoord (5),
  //         bijvoeglijk_naamwoord (2), leenwoorden (3)

  // 4.1 Skill: tegenwoordige_tijd (7 families)
  // Stam, zijn/hebben, inversie (basis/complex), d-stam, meervoud, gebiedende wijs
  {
    familyId: 'SWW-TT-STAM',
    module: 'SpellingWW',
    skill: 'tegenwoordige_tijd',
    subSkill: 'tt_stam_regel',
    tier: 1,
    levelCode: 'B6',
    description: 'Stam bepalen',
  },
  {
    familyId: 'SWW-TT-ZIJN-HEBBEN',
    module: 'SpellingWW',
    skill: 'tegenwoordige_tijd',
    subSkill: 'tt_zijn_hebben',
    tier: 3,
    levelCode: 'M6',
    description: 'Zijn en hebben (is/heeft) (basis)',
  },
  {
    familyId: 'SWW-TT-INV-JIJ',
    module: 'SpellingWW',
    skill: 'tegenwoordige_tijd',
    subSkill: 'tt_inversie_basis',
    tier: 3,
    levelCode: 'M6',
    description: 'Inversie bij jij',
  },
  {
    familyId: 'SWW-TT-DSTAM',
    module: 'SpellingWW',
    skill: 'tegenwoordige_tijd',
    subSkill: 'tt_d_stam',
    tier: 7,
    levelCode: 'B7',
    description: 'D-stam regel (d+t=dt)',
  },
  {
    familyId: 'SWW-TT-INV-TRICK',
    module: 'SpellingWW',
    skill: 'tegenwoordige_tijd',
    subSkill: 'tt_inversie_complex',
    tier: 12,
    levelCode: 'E7+',
    description: 'Inversie complexe gevallen',
  },
  {
    familyId: 'SWW-TT-MEERVOUD',
    module: 'SpellingWW',
    skill: 'tegenwoordige_tijd',
    subSkill: 'tt_meervoud',
    tier: 2,
    levelCode: 'B6+',
    description: 'Meervoud (wij/jullie/zij)',
  },
  {
    familyId: 'SWW-TT-GEBIEDENDE',
    module: 'SpellingWW',
    skill: 'tegenwoordige_tijd',
    subSkill: 'tt_gebiedende_wijs',
    tier: 5,
    levelCode: 'E6',
    description: 'Gebiedende wijs',
  },
  // 4.2 Skill: verleden_tijd (7 families)
  // Sterk, zijn/hebben, kofschip, V→F/Z→S wissel, onregelmatig,
  // hulpwerkwoorden, dubbele letters
  {
    familyId: 'SWW-VT-STERK',
    module: 'SpellingWW',
    skill: 'verleden_tijd',
    subSkill: 'vt_sterk',
    tier: 3,
    levelCode: 'M6',
    description: 'Sterke werkwoorden',
  },
  {
    familyId: 'SWW-VT-ZIJN-HEBBEN',
    module: 'SpellingWW',
    skill: 'verleden_tijd',
    subSkill: 'vt_zijn_hebben',
    tier: 5,
    levelCode: 'E6',
    description: 'Zijn en hebben (was/had)',
  },
  {
    familyId: 'SWW-VT-ZWAK-KOF',
    module: 'SpellingWW',
    skill: 'verleden_tijd',
    subSkill: 'vt_kofschip',
    tier: 5,
    levelCode: 'E6',
    description: "'t Kofschip regel",
  },
  {
    familyId: 'SWW-VT-VZFS',
    module: 'SpellingWW',
    skill: 'verleden_tijd',
    subSkill: 'vt_vz_fs_wissel',
    tier: 9,
    levelCode: 'M7',
    description: 'V→F / Z→S wissel',
  },
  {
    familyId: 'SWW-VT-ONREGELMATIG',
    module: 'SpellingWW',
    skill: 'verleden_tijd',
    subSkill: 'vt_onregelmatig',
    tier: 7,
    levelCode: 'B7',
    description: 'Onregelmatige werkwoorden',
  },
  {
    familyId: 'SWW-VT-ZULLEN-KUNNEN',
    module: 'SpellingWW',
    skill: 'verleden_tijd',
    subSkill: 'vt_hulpwerkwoorden',
    tier: 9,
    levelCode: 'M7',
    description: 'Hulpwerkwoorden (zou/kon)',
  },
  {
    familyId: 'SWW-VT-DUBBEL',
    module: 'SpellingWW',
    skill: 'verleden_tijd',
    subSkill: 'vt_dubbel_dt',
    tier: 11,
    levelCode: 'E7',
    description: 'Dubbele letters (redde/wachtte)',
  },
  // 4.3 Skill: voltooid_deelwoord (5 families)
  // VD met kofschip, sterk, zonder ge-, hulpwerkwoorden, word/wordt verwarring
  {
    familyId: 'SWW-VD-KOF',
    module: 'SpellingWW',
    skill: 'voltooid_deelwoord',
    subSkill: 'vd_kofschip',
    tier: 5,
    levelCode: 'E6',
    description: 'VD met kofschip (ge+stam+t/d)',
  },
  {
    familyId: 'SWW-VD-STERK',
    module: 'SpellingWW',
    skill: 'voltooid_deelwoord',
    subSkill: 'vd_sterk',
    tier: 7,
    levelCode: 'B7',
    description: 'VD van sterke werkwoorden',
  },
  {
    familyId: 'SWW-VD-ZONDER-GE',
    module: 'SpellingWW',
    skill: 'voltooid_deelwoord',
    subSkill: 'vd_zonder_ge',
    tier: 9,
    levelCode: 'M7',
    description: 'VD zonder ge- (be-, ver-, ont-)',
  },
  {
    familyId: 'SWW-VD-HULPWW',
    module: 'SpellingWW',
    skill: 'voltooid_deelwoord',
    subSkill: 'vd_hulpwerkwoorden',
    tier: 9,
    levelCode: 'M7',
    description: 'VD met hulpwerkwoorden (kerndoel)',
  },
  {
    familyId: 'SWW-VD-HOMOFOON',
    module: 'SpellingWW',
    skill: 'voltooid_deelwoord',
    subSkill: 'vd_pv_verwarring',
    tier: 13,
    levelCode: 'B8',
    description: 'Word/wordt verwarring',
  },
  // 4.4 Skill: bijvoeglijk_naamwoord (2 families)
  // BN van VD (lange/korte klank, klinker weglating/medeklinker verdubbeling)
  {
    familyId: 'SWW-BN-VD-KORT',
    module: 'SpellingWW',
    skill: 'bijvoeglijk_naamwoord',
    subSkill: 'bn_klinker_weglating',
    tier: 11,
    levelCode: 'E7',
    description: 'BN van VD (lange klank)',
  },
  {
    familyId: 'SWW-BN-VD-DUBBEL',
    module: 'SpellingWW',
    skill: 'bijvoeglijk_naamwoord',
    subSkill: 'bn_medeklinker_verd',
    tier: 12,
    levelCode: 'E7+',
    description: 'BN van VD (korte klank)',
  },
  // 4.5 Skill: leenwoorden (3 families)
  // Engels (stam/kofschip), Frans
  {
    familyId: 'SWW-LEEN-ENG-STAM',
    module: 'SpellingWW',
    skill: 'leenwoorden',
    subSkill: 'leen_engels_stam',
    tier: 14,
    levelCode: 'M8',
    description: 'Engelse leenwoorden stam',
  },
  {
    familyId: 'SWW-LEEN-ENG-KOF',
    module: 'SpellingWW',
    skill: 'leenwoorden',
    subSkill: 'leen_engels_kofschip',
    tier: 16,
    levelCode: 'E8+',
    description: 'Engelse leenwoorden kofschip',
  },
  {
    familyId: 'SWW-LEEN-FRANS',
    module: 'SpellingWW',
    skill: 'leenwoorden',
    subSkill: 'leen_frans_werkwoorden',
    tier: 15,
    levelCode: 'E8',
    description: 'Franse leenwoorden (verven)',
  },
  // ──────────────────────────────────────────────────────────
  // 5. MODULE: Spelling Regels (SPR) — 20 families, 5 skills
  // ──────────────────────────────────────────────────────────
  // Skills: klankgroepen (6), weetwoorden (3), leenwoorden (5),
  //         samenstelling (2), leestekens (4)

  // 5.1 Skill: klankgroepen (6 families)
  // Open/gesloten lettergreep, medeklinker verdubbelen, klinker verkorten,
  // lange klinkers (aa/ee/oo), au/ou onderscheid
  {
    familyId: 'SPR-KLANK-JAGER',
    module: 'SpellingRegel',
    skill: 'klankgroepen',
    subSkill: 'klank_open_lange',
    tier: 1,
    levelCode: 'B6',
    description: 'Open lettergreep (lange klank)',
  },
  {
    familyId: 'SPR-KLANK-BAKKER',
    module: 'SpellingRegel',
    skill: 'klankgroepen',
    subSkill: 'klank_gesloten_korte',
    tier: 1,
    levelCode: 'B6',
    description: 'Gesloten lettergreep (korte klank)',
  },
  {
    familyId: 'SPR-KLANK-VERDUBBEL',
    module: 'SpellingRegel',
    skill: 'klankgroepen',
    subSkill: 'klank_medeklinker_verdubbel',
    tier: 3,
    levelCode: 'M6',
    description: 'Medeklinker verdubbelen',
  },
  {
    familyId: 'SPR-KLANK-VERKORTEN',
    module: 'SpellingRegel',
    skill: 'klankgroepen',
    subSkill: 'klank_klinker_verkorten',
    tier: 3,
    levelCode: 'M6',
    description: 'Klinker verkorten',
  },
  {
    familyId: 'SPR-KLANK-AA-EE-OO',
    module: 'SpellingRegel',
    skill: 'klankgroepen',
    subSkill: 'klank_lange_klinkers',
    tier: 2,
    levelCode: 'B6+',
    description: 'Lange klinkers (aa/ee/oo)',
  },
  {
    familyId: 'SPR-KLANK-AU-OU',
    module: 'SpellingRegel',
    skill: 'klankgroepen',
    subSkill: 'klank_au_ou',
    tier: 5,
    levelCode: 'E6',
    description: 'Au/ou onderscheid',
  },
  // 5.2 Skill: weetwoorden (3 families)
  // Ei/ij, d/t aan eind, dt-woorden
  {
    familyId: 'SPR-WEET-EI-IJ',
    module: 'SpellingRegel',
    skill: 'weetwoorden',
    subSkill: 'weet_ei_ij',
    tier: 2,
    levelCode: 'B6+',
    description: 'Ei/ij weetwoorden',
  },
  {
    familyId: 'SPR-WEET-D-T',
    module: 'SpellingRegel',
    skill: 'weetwoorden',
    subSkill: 'weet_d_t_eind',
    tier: 3,
    levelCode: 'M6',
    description: 'D/t aan eind woord',
  },
  {
    familyId: 'SPR-WEET-DT',
    module: 'SpellingRegel',
    skill: 'weetwoorden',
    subSkill: 'weet_dt_woorden',
    tier: 5,
    levelCode: 'E6',
    description: 'Dt-woorden (wordt/vindt)',
  },
  // 5.3 Skill: leenwoorden (5 families)
  // C als S/K, Frans (-eau/-oir), Engels, Latijn (meervouden), Grieks
  {
    familyId: 'SPR-LEEN-C-S-K',
    module: 'SpellingRegel',
    skill: 'leenwoorden',
    subSkill: 'leen_c_uitspraak',
    tier: 7,
    levelCode: 'B7',
    description: 'C als S of K',
  },
  {
    familyId: 'SPR-LEEN-FRANS',
    module: 'SpellingRegel',
    skill: 'leenwoorden',
    subSkill: 'leen_frans',
    tier: 10,
    levelCode: 'M7+',
    description: 'Franse leenwoorden (-eau/-oir)',
  },
  {
    familyId: 'SPR-LEEN-ENGELS',
    module: 'SpellingRegel',
    skill: 'leenwoorden',
    subSkill: 'leen_engels',
    tier: 13,
    levelCode: 'B8',
    description: 'Engelse leenwoorden',
  },
  {
    familyId: 'SPR-LEEN-LATIJN',
    module: 'SpellingRegel',
    skill: 'leenwoorden',
    subSkill: 'leen_latijn_mv',
    tier: 15,
    levelCode: 'E8',
    description: 'Klassieke meervouden',
  },
  {
    familyId: 'SPR-LEEN-GRIEKS',
    module: 'SpellingRegel',
    skill: 'leenwoorden',
    subSkill: 'leen_grieks',
    tier: 16,
    levelCode: 'E8+',
    description: 'Griekse leenwoorden',
  },
  // 5.4 Skill: samenstelling (2 families)
  // Tussen-n regel, tussen-s regel
  {
    familyId: 'SPR-SAM-TUSSEN-N',
    module: 'SpellingRegel',
    skill: 'samenstelling',
    subSkill: 'samenst_tussen_n',
    tier: 11,
    levelCode: 'E7',
    description: 'Tussen-n regel',
  },
  {
    familyId: 'SPR-SAM-TUSSEN-S',
    module: 'SpellingRegel',
    skill: 'samenstelling',
    subSkill: 'samenst_tussen_s',
    tier: 13,
    levelCode: 'B8',
    description: 'Tussen-s regel',
  },
  // 5.5 Skill: leestekens (4 families)
  // Hoofdletters, punt/komma, trema, accent aigu
  {
    familyId: 'SPR-HOOFDLETTER',
    module: 'SpellingRegel',
    skill: 'leestekens',
    subSkill: 'hoofdletter_gebruik',
    tier: 1,
    levelCode: 'B6',
    description: 'Hoofdlettergebruik (basis essentieel)',
  },
  {
    familyId: 'SPR-LEESTEKEN-PUNT-KOMMA',
    module: 'SpellingRegel',
    skill: 'leestekens',
    subSkill: 'leesteken_punt_komma',
    tier: 5,
    levelCode: 'E6',
    description: 'Punt en komma gebruik (basis)',
  },
  {
    familyId: 'SPR-TREMA-MV',
    module: 'SpellingRegel',
    skill: 'leestekens',
    subSkill: 'trema_meervoud',
    tier: 9,
    levelCode: 'M7',
    description: 'Trema bij meervoud',
  },
  {
    familyId: 'SPR-ACCENT',
    module: 'SpellingRegel',
    skill: 'leestekens',
    subSkill: 'accent_klemtoon',
    tier: 16,
    levelCode: 'E8+',
    description: 'Accent aigu (vóór/één)',
  },
  // ──────────────────────────────────────────────────────────
  // 6. MODULE: Taalbegrip (TAA) — 35 families, 4 skills
  // ──────────────────────────────────────────────────────────
  // Skills: zinsontleding (10), woordsoorten (8), tekstbegrip (13),
  //         woordenschat (4)
  // NOTE: schrijfvaardigheid skill removed in MVP (open-ended writing not auto-assessable)

  // 6.1 Skill: zinsontleding (10 families)
  // PV/onderwerp, LV/MV, BWB, gezegde, naamwoordelijk deel, voorzetsel,
  // bijzin, hoofdzin, betrekkelijk, inversie
  {
    familyId: 'TAA-ONT-BASIS',
    module: 'Taalbegrip',
    skill: 'zinsontleding',
    subSkill: 'ontl_pv_onderwerp',
    tier: 2,
    levelCode: 'B6+',
    description: 'Persoonsvorm en onderwerp',
  },
  {
    familyId: 'TAA-ONT-LV-MV',
    module: 'Taalbegrip',
    skill: 'zinsontleding',
    subSkill: 'ontl_lv_mv',
    tier: 9,
    levelCode: 'M7',
    description: 'Lijdend en meewerkend voorwerp',
  },
  {
    familyId: 'TAA-ONT-BWB',
    module: 'Taalbegrip',
    skill: 'zinsontleding',
    subSkill: 'ontl_bwb',
    tier: 13,
    levelCode: 'B8',
    description: 'Bijwoordelijke bepalingen',
  },
  {
    familyId: 'TAA-ONT-GEZEGDE',
    module: 'Taalbegrip',
    skill: 'zinsontleding',
    subSkill: 'ontl_gezegde',
    tier: 5,
    levelCode: 'E6',
    description: 'Gezegde herkennen',
  },
  {
    familyId: 'TAA-ONT-NAAMWOORDELIJK',
    module: 'Taalbegrip',
    skill: 'zinsontleding',
    subSkill: 'ontl_naamwoordelijk',
    tier: 11,
    levelCode: 'E7',
    description: 'Naamwoordelijk deel',
  },
  {
    familyId: 'TAA-ONT-VOORZETSEL',
    module: 'Taalbegrip',
    skill: 'zinsontleding',
    subSkill: 'ontl_voorzetsel',
    tier: 7,
    levelCode: 'B7',
    description: 'Voorzetselvoorwerp',
  },
  {
    familyId: 'TAA-ONT-BIJZIN',
    module: 'Taalbegrip',
    skill: 'zinsontleding',
    subSkill: 'ontl_bijzin',
    tier: 13,
    levelCode: 'B8',
    description: 'Bijzinnen herkennen',
  },
  {
    familyId: 'TAA-ONT-HOOFDZIN',
    module: 'Taalbegrip',
    skill: 'zinsontleding',
    subSkill: 'ontl_hoofdzin',
    tier: 11,
    levelCode: 'E7',
    description: 'Hoofdzinnen en nevenschikking',
  },
  {
    familyId: 'TAA-ONT-BETREKKELIJK',
    module: 'Taalbegrip',
    skill: 'zinsontleding',
    subSkill: 'ontl_betrekkelijk',
    tier: 15,
    levelCode: 'E8',
    description: 'Betrekkelijke bijzinnen',
  },
  {
    familyId: 'TAA-ONT-INVERSIE',
    module: 'Taalbegrip',
    skill: 'zinsontleding',
    subSkill: 'ontl_inversie',
    tier: 9,
    levelCode: 'M7',
    description: 'Inversie in zinnen',
  },
  // 6.2 Skill: woordsoorten (8 families)
  // Lidwoord/ZNW, voornaamwoord, BNW, werkwoord, bijwoord, voorzetsel,
  // voegwoord, telwoord
  {
    familyId: 'TAA-SOORT-LID',
    module: 'Taalbegrip',
    skill: 'woordsoorten',
    subSkill: 'srt_lid_znw',
    tier: 3,
    levelCode: 'M6',
    description: 'Lidwoord en zelfstandig naamwoord',
  },
  {
    familyId: 'TAA-SOORT-VNW',
    module: 'Taalbegrip',
    skill: 'woordsoorten',
    subSkill: 'srt_voornaamwoorden',
    tier: 10,
    levelCode: 'M7+',
    description: 'Voornaamwoorden',
  },
  {
    familyId: 'TAA-SOORT-BNW',
    module: 'Taalbegrip',
    skill: 'woordsoorten',
    subSkill: 'srt_bijvoeglijk_nw',
    tier: 5,
    levelCode: 'E6',
    description: 'Bijvoeglijk naamwoord',
  },
  {
    familyId: 'TAA-SOORT-WW',
    module: 'Taalbegrip',
    skill: 'woordsoorten',
    subSkill: 'srt_werkwoord',
    tier: 3,
    levelCode: 'M6',
    description: 'Werkwoord',
  },
  {
    familyId: 'TAA-SOORT-BW',
    module: 'Taalbegrip',
    skill: 'woordsoorten',
    subSkill: 'srt_bijwoord',
    tier: 7,
    levelCode: 'B7',
    description: 'Bijwoord',
  },
  {
    familyId: 'TAA-SOORT-VZ',
    module: 'Taalbegrip',
    skill: 'woordsoorten',
    subSkill: 'srt_voorzetsel',
    tier: 9,
    levelCode: 'M7',
    description: 'Voorzetsel',
  },
  {
    familyId: 'TAA-SOORT-VW',
    module: 'Taalbegrip',
    skill: 'woordsoorten',
    subSkill: 'srt_voegwoord',
    tier: 11,
    levelCode: 'E7',
    description: 'Voegwoord',
  },
  {
    familyId: 'TAA-SOORT-TW',
    module: 'Taalbegrip',
    skill: 'woordsoorten',
    subSkill: 'srt_telwoord',
    tier: 7,
    levelCode: 'B7',
    description: 'Telwoord',
  },
  // 6.3 Skill: tekstbegrip (13 families)
  // Verwijswoorden, hoofdgedachte, feit/mening, verbanden, signaalwoorden,
  // tekstsoorten, detail, conclusie, voorspellen, samenvatten, structuur,
  // doel auteur, argumentatie
  {
    familyId: 'TAA-LEZEN-VERW',
    module: 'Taalbegrip',
    skill: 'tekstbegrip',
    subSkill: 'tekst_verwijswoorden',
    tier: 7,
    levelCode: 'B7',
    description: 'Verwijswoorden',
  },
  {
    familyId: 'TAA-LEZEN-KERN',
    module: 'Taalbegrip',
    skill: 'tekstbegrip',
    subSkill: 'tekst_hoofdgedachte',
    tier: 11,
    levelCode: 'E7',
    description: 'Hoofdgedachte',
  },
  {
    familyId: 'TAA-LEZEN-FEIT',
    module: 'Taalbegrip',
    skill: 'tekstbegrip',
    subSkill: 'tekst_feit_mening',
    tier: 14,
    levelCode: 'M8',
    description: 'Feit en mening',
  },
  {
    familyId: 'TAA-LEZEN-VERB',
    module: 'Taalbegrip',
    skill: 'tekstbegrip',
    subSkill: 'tekst_verbanden',
    tier: 16,
    levelCode: 'E8+',
    description: 'Tekstverbanden',
  },
  {
    familyId: 'TAA-SIGNAALWOORD',
    module: 'Taalbegrip',
    skill: 'tekstbegrip',
    subSkill: 'tekst_signaalwoorden',
    tier: 9,
    levelCode: 'M7',
    description: 'Signaalwoorden herkennen (CITO essentieel)',
  },
  {
    familyId: 'TAA-TEKSTSOORT',
    module: 'Taalbegrip',
    skill: 'tekstbegrip',
    subSkill: 'tekst_tekstsoorten',
    tier: 7,
    levelCode: 'B7',
    description: 'Tekstsoorten onderscheiden (kerndoel)',
  },
  {
    familyId: 'TAA-LEZEN-DETAIL',
    module: 'Taalbegrip',
    skill: 'tekstbegrip',
    subSkill: 'tekst_detail',
    tier: 5,
    levelCode: 'E6',
    description: 'Detailinformatie vinden',
  },
  {
    familyId: 'TAA-LEZEN-CONCLUSIE',
    module: 'Taalbegrip',
    skill: 'tekstbegrip',
    subSkill: 'tekst_conclusie',
    tier: 13,
    levelCode: 'B8',
    description: 'Conclusies trekken',
  },
  {
    familyId: 'TAA-LEZEN-VOORSPEL',
    module: 'Taalbegrip',
    skill: 'tekstbegrip',
    subSkill: 'tekst_voorspellen',
    tier: 9,
    levelCode: 'M7',
    description: 'Voorspellen en verwachten',
  },
  {
    familyId: 'TAA-LEZEN-SAMENVATTEN',
    module: 'Taalbegrip',
    skill: 'tekstbegrip',
    subSkill: 'tekst_samenvatten',
    tier: 11,
    levelCode: 'E7',
    description: 'Samenvatten',
  },
  {
    familyId: 'TAA-LEZEN-STRUCTUUR',
    module: 'Taalbegrip',
    skill: 'tekstbegrip',
    subSkill: 'tekst_structuur',
    tier: 13,
    levelCode: 'B8',
    description: 'Tekststructuur herkennen',
  },
  {
    familyId: 'TAA-LEZEN-DOEL',
    module: 'Taalbegrip',
    skill: 'tekstbegrip',
    subSkill: 'tekst_doel_auteur',
    tier: 15,
    levelCode: 'E8',
    description: 'Doel auteur bepalen',
  },
  {
    familyId: 'TAA-ARGUMENTATIE',
    module: 'Taalbegrip',
    skill: 'tekstbegrip',
    subSkill: 'tekst_argumentatie',
    tier: 13,
    levelCode: 'B8',
    description: 'Argumentatiestructuur (kerndoel)',
  },
  // 6.4 Skill: woordenschat (4 families)
  // Synoniemen, antoniemen, betekenis uit context, idioom/uitdrukkingen
  {
    familyId: 'TAA-WOORD-SYNONIEM',
    module: 'Taalbegrip',
    skill: 'woordenschat',
    subSkill: 'woord_synoniem',
    tier: 7,
    levelCode: 'B7',
    description: 'Synoniemen',
  },
  {
    familyId: 'TAA-WOORD-ANTONIEM',
    module: 'Taalbegrip',
    skill: 'woordenschat',
    subSkill: 'woord_antoniem',
    tier: 7,
    levelCode: 'B7',
    description: 'Antoniemen',
  },
  {
    familyId: 'TAA-WOORD-CONTEXT',
    module: 'Taalbegrip',
    skill: 'woordenschat',
    subSkill: 'woord_betekenis_context',
    tier: 9,
    levelCode: 'M7',
    description: 'Betekenis uit context',
  },
  {
    familyId: 'TAA-WOORD-IDIOOM',
    module: 'Taalbegrip',
    skill: 'woordenschat',
    subSkill: 'woord_idioom',
    tier: 13,
    levelCode: 'B8',
    description: 'Uitdrukkingen en gezegden',
  },
  // ──────────────────────────────────────────────────────────
  // 7. MODULE: Topografie (TOPO) — 21 families, 4 skills
  // ──────────────────────────────────────────────────────────
  // Skills: nederland (6), europa (8), wereld (7), interpretatie (1)
  // NOTE: vaardigheid skill renamed to interpretatie; TOPO-KAART-COOR removed (custom UI)

  // 7.1 Skill: nederland (6 families)
  // Provincies, wateren, steden, landschap, buurlanden, eilanden
  {
    familyId: 'TOPO-NL-PROV',
    module: 'Topo',
    skill: 'nederland',
    subSkill: 'topo_provincies',
    tier: 1,
    levelCode: 'B6',
    description: 'Provincies Nederland',
  },
  {
    familyId: 'TOPO-NL-WATER',
    module: 'Topo',
    skill: 'nederland',
    subSkill: 'topo_wateren',
    tier: 3,
    levelCode: 'M6',
    description: 'Wateren Nederland',
  },
  {
    familyId: 'TOPO-NL-STEDEN',
    module: 'Topo',
    skill: 'nederland',
    subSkill: 'topo_steden_nl',
    tier: 5,
    levelCode: 'E6',
    description: 'Grote steden Nederland',
  },
  {
    familyId: 'TOPO-NL-LANDSCHAP',
    module: 'Topo',
    skill: 'nederland',
    subSkill: 'topo_landschap_nl',
    tier: 7,
    levelCode: 'B7',
    description: 'Landschapstypen Nederland',
  },
  {
    familyId: 'TOPO-NL-BUURLANDEN',
    module: 'Topo',
    skill: 'nederland',
    subSkill: 'topo_buurlanden',
    tier: 3,
    levelCode: 'M6',
    description: 'Buurlanden Nederland',
  },
  {
    familyId: 'TOPO-NL-EILANDEN',
    module: 'Topo',
    skill: 'nederland',
    subSkill: 'topo_eilanden',
    tier: 9,
    levelCode: 'M7',
    description: 'Waddeneilanden',
  },
  // 7.2 Skill: europa (8 families)
  // Landen, hoofdsteden, fysisch, gebergten, rivieren, zeeën, klimaat
  {
    familyId: 'TOPO-EU-LAND',
    module: 'Topo',
    skill: 'europa',
    subSkill: 'topo_landen_eu',
    tier: 7,
    levelCode: 'B7',
    description: 'Landen Europa',
  },
  {
    familyId: 'TOPO-EU-HOOFDST',
    module: 'Topo',
    skill: 'europa',
    subSkill: 'topo_hoofdsteden_eu',
    tier: 7,
    levelCode: 'B7',
    description: 'Hoofdsteden Europa (kerndoel)',
  },
  {
    familyId: 'TOPO-EU-FYSISCH',
    module: 'Topo',
    skill: 'europa',
    subSkill: 'topo_fysisch',
    tier: 9,
    levelCode: 'M7',
    description: 'Fysische geografie Europa',
  },
  {
    familyId: 'TOPO-EU-GEBERGTE',
    module: 'Topo',
    skill: 'europa',
    subSkill: 'topo_gebergte_eu',
    tier: 11,
    levelCode: 'E7',
    description: 'Gebergten Europa',
  },
  {
    familyId: 'TOPO-EU-RIVIEREN',
    module: 'Topo',
    skill: 'europa',
    subSkill: 'topo_rivieren_eu',
    tier: 11,
    levelCode: 'E7',
    description: 'Grote rivieren Europa',
  },
  {
    familyId: 'TOPO-EU-ZEEEN',
    module: 'Topo',
    skill: 'europa',
    subSkill: 'topo_zeeen_eu',
    tier: 9,
    levelCode: 'M7',
    description: 'Zeeën en oceanen Europa',
  },
  {
    familyId: 'TOPO-EU-KLIMAAT',
    module: 'Topo',
    skill: 'europa',
    subSkill: 'topo_klimaat_eu',
    tier: 13,
    levelCode: 'B8',
    description: 'Klimaatzones Europa',
  },
  {
    familyId: 'TOPO-EU-BUURLANDEN',
    module: 'Topo',
    skill: 'europa',
    subSkill: 'topo_buurlanden_eu',
    tier: 9,
    levelCode: 'M7',
    description: 'Buurlanden van EU-landen (grenzen)',
  },
  // 7.3 Skill: wereld (7 families)
  // Continenten/oceanen, landen, hoofdsteden, rivieren, gebergten
  {
    familyId: 'TOPO-WERELD-CONT',
    module: 'Topo',
    skill: 'wereld',
    subSkill: 'topo_continenten',
    tier: 11,
    levelCode: 'E7',
    description: 'Continenten en oceanen',
  },
  {
    familyId: 'TOPO-WERELD-LANDEN',
    module: 'Topo',
    skill: 'wereld',
    subSkill: 'topo_landen_wereld',
    tier: 13,
    levelCode: 'B8',
    description: 'Belangrijke landen wereld',
  },
  {
    familyId: 'TOPO-WERELD-HOOFDST',
    module: 'Topo',
    skill: 'wereld',
    subSkill: 'topo_hoofdsteden_wereld',
    tier: 9,
    levelCode: 'M7',
    description: 'Wereldhoofdsteden (kerndoel)',
  },
  {
    familyId: 'TOPO-WERELD-RIVIER',
    module: 'Topo',
    skill: 'wereld',
    subSkill: 'topo_rivieren_wereld',
    tier: 9,
    levelCode: 'M7',
    description: 'Grote rivieren wereld (kerndoel)',
  },
  {
    familyId: 'TOPO-WERELD-GEBERGTE',
    module: 'Topo',
    skill: 'wereld',
    subSkill: 'topo_gebergte_wereld',
    tier: 11,
    levelCode: 'E7',
    description: 'Wereldgebergten (kerndoel)',
  },
  {
    familyId: 'TOPO-WERELD-OCEANEN',
    module: 'Topo',
    skill: 'wereld',
    subSkill: 'topo_oceanen_wereld',
    tier: 9,
    levelCode: 'M7',
    description: 'Oceanen en grote zeeën wereld',
  },
  {
    familyId: 'TOPO-WERELD-KLIMAAT',
    module: 'Topo',
    skill: 'wereld',
    subSkill: 'topo_klimaat_wereld',
    tier: 13,
    levelCode: 'B8',
    description: 'Klimaatzones wereld',
  },
  // 7.4 Skill: interpretatie (1 family)
  // Klimaatzones interpreteren (renamed from vaardigheid for clarity)
  {
    familyId: 'TOPO-KLIMAAT',
    module: 'Topo',
    skill: 'interpretatie',
    subSkill: 'klimaat_zones',
    tier: 15,
    levelCode: 'E8',
    description: 'Klimaatzones interpreteren',
  },
  // ──────────────────────────────────────────────────────────
  // 8. MODULE: Studievaardigheden (STU) — 21 families, 5 skills
  // ──────────────────────────────────────────────────────────
  // Skills: informatie (5), schemas (7), kaartlezen (2), leerstrategieen (4), presenteren (1)
  // NOTE: STU-BRONNEN removed (complex critical thinking assessment)
  // NOTE: STU-PRESENTATIE removed (open-ended presentation not auto-assessable)

  // 8.1 Skill: informatie (5 families)
  // Alfabetiseren, inhoudsopgave, woordenboek, internet, bibliotheek
  // NOTE: STU-BRONNEN removed (complex critical thinking assessment)
  {
    familyId: 'STU-ALFABET',
    module: 'Studievaardig',
    skill: 'informatie',
    subSkill: 'info_alfabetiseren',
    tier: 2,
    levelCode: 'B6+',
    description: 'Alfabetiseren',
  },
  {
    familyId: 'STU-INHOUDSOPGAVE',
    module: 'Studievaardig',
    skill: 'informatie',
    subSkill: 'info_inhoudsopgave',
    tier: 1,
    levelCode: 'B6',
    description: 'Inhoudsopgave gebruiken (basis essentieel)',
  },
  {
    familyId: 'STU-WOORDENBOEK',
    module: 'Studievaardig',
    skill: 'informatie',
    subSkill: 'info_woordenboek',
    tier: 3,
    levelCode: 'M6',
    description: 'Woordenboek gebruiken (kerndoel)',
  },
  {
    familyId: 'STU-INTERNET',
    module: 'Studievaardig',
    skill: 'informatie',
    subSkill: 'info_internet',
    tier: 9,
    levelCode: 'M7',
    description: 'Zoeken op internet',
  },
  {
    familyId: 'STU-BIBLIOTHEEK',
    module: 'Studievaardig',
    skill: 'informatie',
    subSkill: 'info_bibliotheek',
    tier: 7,
    levelCode: 'B7',
    description: 'Bibliotheek systeem',
  },
  // 8.2 Skill: schemas (7 families — added tijdlijn + flowchart)
  // Tabellen, grafieken, diagrammen (staaf/lijn/cirkel), tijdlijn, flowchart
  {
    familyId: 'STU-TABEL',
    module: 'Studievaardig',
    skill: 'schemas',
    subSkill: 'schema_tabel',
    tier: 5,
    levelCode: 'E6',
    description: 'Tabellen lezen',
  },
  {
    familyId: 'STU-GRAFIEK',
    module: 'Studievaardig',
    skill: 'schemas',
    subSkill: 'schema_grafiek',
    tier: 7,
    levelCode: 'B7',
    description: 'Grafieken interpreteren',
  },
  {
    familyId: 'STU-DIAGRAM',
    module: 'Studievaardig',
    skill: 'schemas',
    subSkill: 'schema_diagram',
    tier: 7,
    levelCode: 'B7',
    description: 'Diagrammen lezen (kerndoel)',
  },
  {
    familyId: 'STU-STAAF',
    module: 'Studievaardig',
    skill: 'schemas',
    subSkill: 'schema_staafdiagram',
    tier: 7,
    levelCode: 'B7',
    description: 'Staafdiagram lezen',
  },
  {
    familyId: 'STU-LIJN',
    module: 'Studievaardig',
    skill: 'schemas',
    subSkill: 'schema_lijndiagram',
    tier: 9,
    levelCode: 'M7',
    description: 'Lijndiagram lezen',
  },
  {
    familyId: 'STU-CIRKEL',
    module: 'Studievaardig',
    skill: 'schemas',
    subSkill: 'schema_cirkeldiagram',
    tier: 9,
    levelCode: 'M7',
    description: 'Cirkeldiagram lezen',
  },
  {
    familyId: 'STU-TIJDLIJN',
    module: 'Studievaardig',
    skill: 'schemas',
    subSkill: 'schema_tijdlijn',
    tier: 7,
    levelCode: 'B7',
    description: 'Tijdlijn lezen en interpreteren',
  },
  {
    familyId: 'STU-FLOWCHART',
    module: 'Studievaardig',
    skill: 'schemas',
    subSkill: 'schema_flowchart',
    tier: 11,
    levelCode: 'E7',
    description: 'Flowchart lezen en interpreteren',
  },
  // 8.3 Skill: kaartlezen (2 families)
  // Legenda/route, route plannen
  {
    familyId: 'STU-KAART',
    module: 'Studievaardig',
    skill: 'kaartlezen',
    subSkill: 'kaart_legenda_route',
    tier: 9,
    levelCode: 'M7',
    description: 'Kaartlezen (legenda/schaal)',
  },
  {
    familyId: 'STU-ROUTE',
    module: 'Studievaardig',
    skill: 'kaartlezen',
    subSkill: 'kaart_route_plannen',
    tier: 11,
    levelCode: 'E7',
    description: 'Route plannen op kaart',
  },
  // 8.4 Skill: leerstrategieen (4 families)
  // Notities, samenvatten, mindmaps, plannen/organiseren
  {
    familyId: 'STU-NOTITIES',
    module: 'Studievaardig',
    skill: 'leerstrategieen',
    subSkill: 'leer_notities',
    tier: 9,
    levelCode: 'M7',
    description: 'Notities maken',
  },
  {
    familyId: 'STU-SAMENVATTEN',
    module: 'Studievaardig',
    skill: 'leerstrategieen',
    subSkill: 'leer_samenvatten',
    tier: 9,
    levelCode: 'M7',
    description: 'Teksten samenvatten (kerndoel)',
  },
  {
    familyId: 'STU-MINDMAP',
    module: 'Studievaardig',
    skill: 'leerstrategieen',
    subSkill: 'leer_mindmap',
    tier: 13,
    levelCode: 'B8',
    description: 'Mindmaps maken',
  },
  {
    familyId: 'STU-PLANNEN',
    module: 'Studievaardig',
    skill: 'leerstrategieen',
    subSkill: 'leer_plannen',
    tier: 11,
    levelCode: 'E7',
    description: 'Werk plannen en organiseren',
  },
  // 8.5 Skill: presenteren (1 family)
  // Informatie combineren en presenteren
  {
    familyId: 'STU-COMBINEER',
    module: 'Studievaardig',
    skill: 'presenteren',
    subSkill: 'info_combineren',
    tier: 9,
    levelCode: 'M7',
    description: 'Informatie uit meerdere bronnen combineren en presenteren',
  },
]

// ============================================================
// SELF-VALIDATION (runs at module load)
// ============================================================
// Ensures the TAXONOMY array itself is internally consistent.
// This catches typos in the registry before any question validation.
;(function validateTaxonomyIntegrity() {
  const errors: string[] = []
  const seenFamilyIds = new Set<string>()
  const seenSubSkills = new Set<string>()
  for (const family of TAXONOMY) {
    // Unique familyId
    if (seenFamilyIds.has(family.familyId)) {
      errors.push(`Duplicate familyId: ${family.familyId}`)
    }
    seenFamilyIds.add(family.familyId)

    // Unique subSkill
    if (seenSubSkills.has(family.subSkill)) {
      errors.push(
        `Duplicate subSkill: ${family.subSkill} (in ${family.familyId})`,
      )
    }
    seenSubSkills.add(family.subSkill)

    // Valid tier
    if (!isValidTier(family.tier)) {
      errors.push(`Invalid tier ${family.tier} for ${family.familyId}`)
    }

    // Tier ↔ levelCode sync
    if (!isTierNiveauSynced(family.tier, family.levelCode)) {
      const expected = tierToNiveauCode(family.tier)
      errors.push(
        `Tier/levelCode mismatch for ${family.familyId}: tier ${family.tier} should be "${expected}", got "${family.levelCode}"`,
      )
    }
  }
  if (errors.length > 0) {
    logError('🚨 TAXONOMY REGISTRY INTEGRITY ERRORS:')
    errors.forEach((e) => logError(`  ❌ ${e}`))
    if (process.env.NODE_ENV === 'development') {
      throw new Error(
        `Taxonomy registry has ${errors.length} integrity error(s). Fix before proceeding.`,
      )
    }
  } else {
    debug(
      `✅ Taxonomy Registry validated: ${TAXONOMY.length} families, ${seenFamilyIds.size} unique IDs`,
    )
  }
})()

// ============================================================
// Lookup Maps (built once at module load)
// ============================================================

/** Primary lookup: canonical 4.1 familyId → FamilyDefinition */
export const FAMILY_BY_ID = new Map<string, FamilyDefinition>(
  TAXONOMY.map((f) => [f.familyId, f]),
)

/** SubSkill → FamilyDefinition (1:1 in 4.1) */
export const FAMILY_BY_SUBSKILL = new Map<string, FamilyDefinition>(
  TAXONOMY.map((f) => [f.subSkill, f]),
)

/** Module-grouped families */
export const FAMILIES_BY_MODULE = new Map<ModuleId, FamilyDefinition[]>()

/**
 * Module-scoped skill lookup
 * Key format: "ModuleId::skill" (e.g., "TijdGeld::snelheid", "SpellingWW::leenwoorden")
 * This prevents skill name collisions across modules.
 */
export const FAMILIES_BY_MODULE_SKILL = new Map<string, FamilyDefinition[]>()
TAXONOMY.forEach((f) => {
  // By module
  if (!FAMILIES_BY_MODULE.has(f.module)) {
    FAMILIES_BY_MODULE.set(f.module, [])
  }
  FAMILIES_BY_MODULE.get(f.module)!.push(f)

  // By module-scoped skill (NEW - Option B implementation)
  const moduleScopedKey = `${f.module}::${f.skill}`
  if (!FAMILIES_BY_MODULE_SKILL.has(moduleScopedKey)) {
    FAMILIES_BY_MODULE_SKILL.set(moduleScopedKey, [])
  }
  FAMILIES_BY_MODULE_SKILL.get(moduleScopedKey)!.push(f)
})

// ============================================================
// Lookup Helpers
// ============================================================

/**
 * Check if a familyId exists in the taxonomy.
 */
export function isValidFamilyId(id: string): boolean {
  return FAMILY_BY_ID.has(id)
}

/**
 * Get the FamilyDefinition for a familyId.
 */
export function getFamilyDefinition(id: string): FamilyDefinition | undefined {
  return FAMILY_BY_ID.get(id)
}

/**
 * Get all families for a module.
 */
export function getFamiliesForModule(moduleId: ModuleId): FamilyDefinition[] {
  return FAMILIES_BY_MODULE.get(moduleId) || []
}

/**
 * Get the tier for a familyId.
 */
export function getTierForFamily(familyId: string): number | undefined {
  return getFamilyDefinition(familyId)?.tier
}

/**
 * Check if a subSkill exists in the taxonomy.
 */
export function isValidSubSkill(subSkill: string): boolean {
  return FAMILY_BY_SUBSKILL.has(subSkill)
}

/**
 * Get the FamilyDefinition for a subSkill.
 */
export function getFamilyBySubSkill(
  subSkill: string,
): FamilyDefinition | undefined {
  return FAMILY_BY_SUBSKILL.get(subSkill)
}

/**
 * Validate that a familyId, module, and subSkill are consistent.
 * Returns an array of error strings (empty = valid).
 */
export function validateFamilyConsistency(
  familyId: string,
  module: ModuleId,
  subSkill: string,
): string[] {
  const errors: string[] = []
  const def = getFamilyDefinition(familyId)
  if (!def) {
    errors.push(`Unknown familyId: "${familyId}"`)
    return errors
  }
  if (def.module !== module) {
    errors.push(
      `Module mismatch for ${familyId}: taxonomy says "${def.module}", got "${module}"`,
    )
  }
  if (def.subSkill !== subSkill) {
    errors.push(
      `SubSkill mismatch for ${familyId}: taxonomy says "${def.subSkill}", got "${subSkill}"`,
    )
  }
  return errors
}

/**
 * Get the authoritative tier for a familyId.
 * Questions MUST use this tier — they cannot override it.
 */
export function getAuthoritativeTier(familyId: string): number | undefined {
  return getFamilyDefinition(familyId)?.tier
}

/**
 * Get the authoritative levelCode for a familyId.
 */
export function getAuthoritativeLevelCode(
  familyId: string,
): string | undefined {
  return getFamilyDefinition(familyId)?.levelCode
}

/**
 * Get all skills for a module (unique list).
 */
export function getSkillsForModule(moduleId: ModuleId): string[] {
  const families = getFamiliesForModule(moduleId)
  return [...new Set(families.map((f) => f.skill))]
}

/**
 * Create a module-scoped skill key.
 * Format: "ModuleId::skill"
 *
 * @param moduleId - The module identifier
 * @param skill - The skill name
 * @returns Module-scoped key (e.g., "TijdGeld::snelheid")
 */
export function getModuleScopedSkillKey(
  moduleId: ModuleId,
  skill: string,
): string {
  assertModuleScopedSkillInput(skill)
  return `${moduleId}::${skill}`
}
function assertModuleScopedSkillInput(skill: string): void {
  if (skill.includes('::')) {
    throw new Error(
      `Expected bare skill name without module scope, received "${skill}". Pass module and skill separately.`,
    )
  }
}

/**
 * Get all families for a specific skill within a module.
 * This is the PREFERRED method for skill queries (module-scoped).
 *
 * @param moduleId - The module identifier
 * @param skill - The skill name
 * @returns Array of FamilyDefinition objects for that module-scoped skill
 */
export function getFamiliesForModuleSkill(
  moduleId: ModuleId,
  skill: string,
): FamilyDefinition[] {
  const key = getModuleScopedSkillKey(moduleId, skill)
  return FAMILIES_BY_MODULE_SKILL.get(key) || []
}

/**
 * Get all unique module-scoped skills across all modules.
 * Returns keys in format "ModuleId::skill"
 *
 * @returns Array of module-scoped skill keys
 */
export function getAllModuleScopedSkills(): string[] {
  return Array.from(FAMILIES_BY_MODULE_SKILL.keys())
}

/**
 * Check if a module-scoped skill exists.
 *
 * @param moduleId - The module identifier
 * @param skill - The skill name
 * @returns true if the module-scoped skill exists
 */
export function isValidModuleSkill(moduleId: ModuleId, skill: string): boolean {
  const key = getModuleScopedSkillKey(moduleId, skill)
  return FAMILIES_BY_MODULE_SKILL.has(key)
}

/**
 * Get skill statistics for a module.
 *
 * @param moduleId - The module to analyze
 * @returns Object with skill counts and family distribution
 */
export function getSkillStatsForModule(moduleId: ModuleId) {
  const families = getFamiliesForModule(moduleId)
  const skillGroups = new Map<string, number>()
  families.forEach((f) => {
    skillGroups.set(f.skill, (skillGroups.get(f.skill) || 0) + 1)
  })
  return {
    module: moduleId,
    totalSkills: skillGroups.size,
    totalFamilies: families.length,
    skillBreakdown: Array.from(skillGroups.entries())
      .map(([skill, count]) => ({
        skill,
        moduleScopedKey: getModuleScopedSkillKey(moduleId, skill),
        familyCount: count,
      }))
      .sort((a, b) => b.familyCount - a.familyCount),
  }
}

/**
 * Validate skill consistency across the taxonomy.
 * Ensures that skill groupings are logical and complete.
 *
 * @returns Array of validation warnings (empty = all good)
 */
export function validateSkillConsistency(): string[] {
  const warnings: string[] = []

  // Check for orphaned skills (skills with only 1 family might be miscategorized)
  FAMILIES_BY_MODULE_SKILL.forEach((families, moduleScopedKey) => {
    if (families.length === 1) {
      warnings.push(
        `Module-scoped skill "${moduleScopedKey}" has only 1 family (${families[0].familyId}). Consider if this should be grouped differently.`,
      )
    }
  })

  // Check for skill naming consistency (should be lowercase with underscores)
  FAMILIES_BY_MODULE_SKILL.forEach((families, moduleScopedKey) => {
    const skill = moduleScopedKey.split('::')[1]
    if (skill !== skill.toLowerCase()) {
      warnings.push(
        `Skill "${skill}" in ${moduleScopedKey} should be lowercase`,
      )
    }
    if (skill.includes('-') || skill.includes(' ')) {
      warnings.push(
        `Skill "${skill}" in ${moduleScopedKey} should use underscores, not hyphens or spaces`,
      )
    }
  })

  // Check for skill name collisions across modules (informational, not an error)
  const skillNameCounts = new Map<string, Set<ModuleId>>()
  TAXONOMY.forEach((f) => {
    if (!skillNameCounts.has(f.skill)) {
      skillNameCounts.set(f.skill, new Set())
    }
    skillNameCounts.get(f.skill)!.add(f.module)
  })
  skillNameCounts.forEach((modules, skill) => {
    if (modules.size > 1) {
      warnings.push(
        `INFO: Skill name "${skill}" appears in multiple modules: ${Array.from(modules).join(', ')}. This is OK with module-scoped architecture.`,
      )
    }
  })
  return warnings
}

/**
 * Get the largest skills by family count (module-scoped).
 * Useful for understanding which skills have the most content.
 *
 * @param limit - Maximum number of skills to return (default: 10)
 * @returns Array of module-scoped skills with their family counts, sorted descending
 */
export function getLargestSkills(limit: number = 10): Array<{
  moduleScopedKey: string
  skill: string
  familyCount: number
  module: ModuleId
}> {
  const skillSizes: Array<{
    moduleScopedKey: string
    skill: string
    familyCount: number
    module: ModuleId
  }> = []
  FAMILIES_BY_MODULE_SKILL.forEach((families, moduleScopedKey) => {
    const module = families[0].module
    const skill = families[0].skill
    skillSizes.push({
      moduleScopedKey,
      skill,
      familyCount: families.length,
      module,
    })
  })
  return skillSizes
    .sort((a, b) => b.familyCount - a.familyCount)
    .slice(0, limit)
}

/**
 * Check if a family has implemented content (questions exist).
 * This checks if questions are available for a familyId.
 *
 * @param familyId - The family to check
 * @returns true if questions exist, false otherwise
 */
export function hasFamilyContent(familyId: string): boolean {
  // TODO: Implement by checking if questions exist for this familyId
  // For now, return true for all existing families
  // This would need to query the question loader to check for actual content
  return isValidFamilyId(familyId)
}

/**
 * Get statistics about the taxonomy.
 * Provides comprehensive overview of the taxonomy structure.
 */
export function getTaxonomyStats() {
  const moduleStats = Array.from(FAMILIES_BY_MODULE.entries()).map(
    ([moduleId, families]) => {
      const skills = new Set(families.map((f) => f.skill))
      return {
        module: moduleId,
        totalFamilies: families.length,
        totalSkills: skills.size,
        skillBreakdown: Array.from(skills)
          .map((skill) => ({
            skill,
            moduleScopedKey: getModuleScopedSkillKey(moduleId, skill),
            familyCount: families.filter((f) => f.skill === skill).length,
          }))
          .sort((a, b) => b.familyCount - a.familyCount),
      }
    },
  )
  return {
    totalFamilies: TAXONOMY.length,
    totalModules: FAMILIES_BY_MODULE.size,
    totalModuleScopedSkills: FAMILIES_BY_MODULE_SKILL.size,
    moduleBreakdown: moduleStats,
    largestSkills: getLargestSkills(10),
    // Hierarchy summary
    hierarchy: {
      description: 'Module → Skill → SubSkill → FamilyId',
      rules: {
        modules: '8 fixed modules (broad subject areas)',
        skills:
          '~38 module-scoped skills total (~5-10 per module, thematic groupings)',
        subSkills: '244 subSkills (1:1 with families, specific competencies)',
        familyIds: '244 unique identifiers (UPPERCASE-HYPHEN format)',
      },
      overlap: {
        skills: 'CAN overlap within a module - multiple families per skill',
        skillsAcrossModules:
          'MODULE-SCOPED - same name in different modules = different skills',
        subSkills: 'NEVER overlap - strict 1:1 with familyId',
        familyIds: 'NEVER overlap - absolutely unique',
      },
      moduleScopedArchitecture: {
        enabled: true,
        format:
          'ModuleId::skill (e.g., "TijdGeld::snelheid", "SpellingWW::leenwoorden")',
        benefit: 'Prevents skill name collisions across modules',
        example:
          'SpellingWW::leenwoorden (3 families) ≠ SpellingRegel::leenwoorden (5 families)',
      },
    },
  }
}
